import { useMutation } from '@apollo/client'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { isNil } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Loader from '../../../common/loader/Loader'
import { EmptyCampaigns } from '../../../common/campaign/EmptyCampaigns'
import { Campaign } from '../../../common/types/campaigns'
import { Query } from '../../../react-apollo/Components'
import authStore from '../../auth/authStore'
import { User } from '../../auth/types'
import { i18n } from '../../index/index'
import {
  addNewCampaignMutation,
  addNewStationCampaignMutation,
  fetchAdminCampaigns,
  setCampaignVisibilityMutation,
  updateCampaignMutation,
  updateStationCampaignMutation,
} from './campaignQueries'

export default function CampaignsPage() {
  const { stationId } = useParams<{ stationId?: string }>()
  const now = moment().toISOString()
  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false)

  return (
    <Container sx={{ marginTop: '80px' }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        rowGap={1}
        columnGap={1}
        sx={{ mb: 3 }}
      >
        <Typography variant='h4'>{i18n.t('campaigns.heading')}</Typography>
        <Button variant='contained' color='success' size='small' onClick={() => setIsNewCampaignOpen(true)}>
          {i18n.t('campaigns.addNewCampaign')}
        </Button>
      </Stack>
      <Query<
        { campaigns?: Campaign[]; campaignsAfterDate?: Campaign[] },
        { date: string; stationId?: string; includeChainCampaigns?: boolean }
      >
        query={fetchAdminCampaigns}
        variables={{ date: now, stationId, includeChainCampaigns: true }}
      >
        {({ data, loading, refetch }) => {
          return (
            <>
              <NewCampaignForm
                stationId={stationId}
                refetch={refetch}
                open={isNewCampaignOpen}
                onClose={() => setIsNewCampaignOpen(false)}
              />
              <Loader loading={loading && !data}>
                <Stack spacing={4}>
                  <Stack spacing={1.5}>
                    <Typography variant='h6'>{i18n.t('campaigns.currentCampaigns')}</Typography>
                    {(data?.campaigns?.length ?? 0) > 0 ? (
                      <CampaignTable
                        campaigns={data?.campaigns ?? []}
                        stationId={stationId}
                        user={authStore.user}
                        refetch={refetch}
                      />
                    ) : (
                      <EmptyCampaigns />
                    )}
                  </Stack>
                  <Stack spacing={1.5}>
                    <Typography variant='h6'>{i18n.t('campaigns.futureCampaigns')}</Typography>
                    {(data?.campaignsAfterDate?.length ?? 0) > 0 ? (
                      <CampaignTable
                        campaigns={data?.campaignsAfterDate ?? []}
                        stationId={stationId}
                        user={authStore.user}
                        refetch={refetch}
                      />
                    ) : (
                      <EmptyCampaigns />
                    )}
                  </Stack>
                </Stack>
              </Loader>
            </>
          )
        }}
      </Query>
    </Container>
  )
}
function CampaignTable({
  campaigns,
  stationId,
  user,
  refetch,
}: {
  campaigns: Campaign[]
  stationId?: string
  user?: User | null
  refetch: (...args: Array<any>) => any
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) // < 600px

  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | null>(null)
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false)

  const [setCampaignVisibility] = useMutation(setCampaignVisibilityMutation)

  function onCloseEditCampaign() {
    setIsEditCampaignOpen(false)
  }

  function openEditCampaign(campaign: Campaign) {
    setCampaignToEdit(campaign)
    setIsEditCampaignOpen(true)
  }

  function canEditCampaign(campaign: Campaign, user: User | null | undefined) {
    if (isNil(user)) {
      return false
    }
    // Chain (global) campaigns have no stationId. They can only be edited from the
    // chain admin view (no stationId in the route) by a chain admin, via updateCampaign.
    if (!campaign.stationId) {
      return !stationId && user.permissions.includes('manage_chain')
    }
    // Station-scoped campaigns can be edited only from their OWN station's admin view,
    // by a station manager, via updateStationCampaign. A station page may also list
    // chain/store-group campaigns (stationId null, handled above) which are NOT locally
    // created here and must stay non-editable to avoid a "Campaign not found" error.
    return campaign.stationId === stationId && user.permissions.includes('manage_ownStation')
  }

  // Visibility is a per-station dashboard override: a station manager may hide/show ANY
  // campaign (including chain campaigns) for their own station. This is intentionally broader
  // than edit permissions, which only allow chain edits for chain admins.
  const canToggleVisibility = !isNil(user) && !!stationId && user.permissions.includes('manage_ownStation')

  function onToggleVisibility(campaign: Campaign) {
    if (!stationId) {
      return
    }
    setCampaignVisibility({
      variables: {
        campaignId: campaign.id,
        stationId,
        isVisible: !(campaign.isVisible ?? true),
      },
    }).then(() => refetch())
  }

  // ---------- Mobile: card list (mirrors the dashboard CampaignTable pattern) ----------
  const renderMobileList = () => (
    <Stack component='ul' spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {(campaigns ?? []).map((campaign: Campaign) => (
        <Card component='li' key={campaign.id} elevation={1}>
          <CardHeader
            sx={{ pb: campaign.description ? 0 : 2 }}
            title={campaign.name}
            titleTypographyProps={{
              variant: 'subtitle1',
              sx: { wordBreak: 'break-word', lineHeight: 1.3 },
            }}
            subheader={
              <>
                {moment(campaign.startTime).format('D.M.YYYY')}
                {' – '}
                {moment(campaign.endTime).format('D.M.YYYY')}
              </>
            }
            subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
          />
          <CardContent sx={{ pt: 1, '&:last-child': { pb: 2 } }}>
            {campaign.description && (
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                {campaign.description}
              </Typography>
            )}
            {(canToggleVisibility || canEditCampaign(campaign, user)) && (
              <>
                <Divider sx={{ mt: campaign.description ? 1.5 : 0.5, mb: 1 }} />
                <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
                  {canToggleVisibility ? (
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <Typography variant='body2' color='text.secondary'>
                        {i18n.t('campaigns.visibility')}
                      </Typography>
                      <Switch
                        checked={campaign.isVisible ?? true}
                        onChange={() => onToggleVisibility(campaign)}
                        inputProps={{ 'aria-label': i18n.t('campaigns.visibility') }}
                      />
                    </Stack>
                  ) : (
                    <span />
                  )}
                  {canEditCampaign(campaign, user) && (
                    <Button
                      size='small'
                      startIcon={<EditIcon fontSize='small' />}
                      onClick={() => openEditCampaign(campaign)}
                    >
                      {i18n.t('buttons.edit')}
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  )

  // ---------- Tablet + Desktop: table with horizontal scroll fallback ----------
  const renderTable = () => (
    <Paper>
      <TableContainer
        component={Box}
        sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch', // smooth scrolling on iOS
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 520, md: 700 },
            '& .MuiTableCell-root': {
              px: { sm: 1, md: 2 },
              py: { sm: 1, md: 1.5 },
              fontSize: { sm: 13, md: 14 },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t('campaigns.name')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{i18n.t('campaigns.starts')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{i18n.t('campaigns.ends')}</TableCell>
              <TableCell sx={{ maxWidth: 240 }}>{i18n.t('campaigns.description')}</TableCell>
              {canToggleVisibility && <TableCell>{i18n.t('campaigns.visibility')}</TableCell>}
              <TableCell>{i18n.t('buttons.edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(campaigns ?? []).map((campaign: Campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>{campaign.name}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{moment(campaign.startTime).format('D.M.YYYY')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{moment(campaign.endTime).format('D.M.YYYY')}</TableCell>
                <TableCell sx={{ maxWidth: 240, wordBreak: 'break-word' }}>{campaign.description}</TableCell>
                {canToggleVisibility && (
                  <TableCell>
                    <Switch
                      checked={campaign.isVisible ?? true}
                      onChange={() => onToggleVisibility(campaign)}
                      inputProps={{ 'aria-label': i18n.t('campaigns.visibility') }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  {canEditCampaign(campaign, user) && (
                    <IconButton size='small' onClick={() => openEditCampaign(campaign)}>
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )

  return (
    <>
      {isMobile ? renderMobileList() : renderTable()}
      <EditCampaignForm
        campaign={campaignToEdit}
        onClose={onCloseEditCampaign}
        open={isEditCampaignOpen}
        stationId={stationId}
      />
    </>
  )
}

// Shared form body (title + fields) used by both the "new" and "edit" campaign dialogs,
// so the two stay visually and behaviourally identical.
function CampaignFormFields({
  title,
  name,
  starts,
  ends,
  description,
  onNameChange,
  onStartsChange,
  onEndsChange,
  onDescriptionChange,
}: {
  title: string
  name: string
  starts: moment.Moment | null
  ends: moment.Moment | null
  description: string
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onStartsChange: (value: moment.Moment | null) => void
  onEndsChange: (value: moment.Moment | null) => void
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <Box>
      <Typography variant='h6'>{title}</Typography>
      <Paper sx={{ padding: 2 }}>
        <Stack spacing={2} direction='column'>
          <TextField
            label={i18n.t('campaigns.name')}
            variant='outlined'
            onChange={onNameChange}
            value={name}
            fullWidth
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <DatePicker
                label={i18n.t('campaigns.starts')}
                value={starts}
                onChange={onStartsChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label={i18n.t('campaigns.ends')}
                value={ends}
                onChange={onEndsChange}
                minDate={starts ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
          </LocalizationProvider>
          <TextField
            label={i18n.t('campaigns.description')}
            variant='outlined'
            value={description}
            onChange={onDescriptionChange}
            fullWidth
            multiline
            rows={5}
          />
        </Stack>
      </Paper>
    </Box>
  )
}

function NewCampaignForm({
  stationId,
  refetch,
  open,
  onClose,
}: {
  stationId?: string
  refetch: (...args: Array<any>) => any
  open: boolean
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [starts, setStarts] = useState<moment.Moment | null>(null)
  const [ends, setEnds] = useState<moment.Moment | null>(null)
  const [description, setDescription] = useState('')

  const [addCampaign, result] = useMutation(stationId ? addNewStationCampaignMutation : addNewCampaignMutation)

  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value)
  }

  function onDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDescription(e.target.value)
  }

  function resetForm() {
    setName('')
    setStarts(null)
    setEnds(null)
    setDescription('')
  }

  function onCancel() {
    resetForm()
    onClose()
  }

  function onSubmit() {
    addCampaign({
      variables: {
        stationId,
        input: {
          name,
          startTime: starts?.toISOString(),
          endTime: ends?.toISOString(),
          description: description,
        },
      },
    }).then(() => {
      resetForm()
      refetch()
      onClose()
    })
  }

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth='sm'>
      <DialogContent>
        <CampaignFormFields
          title={i18n.t('campaigns.addNewCampaign')}
          name={name}
          starts={starts}
          ends={ends}
          description={description}
          onNameChange={onNameChange}
          onStartsChange={setStarts}
          onEndsChange={setEnds}
          onDescriptionChange={onDescriptionChange}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={onCancel}>
          {i18n.t('buttons.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!name || !starts || !ends || result.loading}
          onClick={onSubmit}
        >
          {i18n.t('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function EditCampaignForm({
  campaign,
  onClose,
  open,
  stationId,
}: {
  campaign: Campaign | null
  onClose: () => void
  open: boolean
  stationId?: string
}) {
  const [name, setName] = useState(campaign?.name)
  const [starts, setStarts] = useState<moment.Moment | null>(moment(campaign?.startTime))
  const [ends, setEnds] = useState<moment.Moment | null>(moment(campaign?.endTime))
  const [description, setDescription] = useState(campaign?.description ?? '')

  const [updateCampaign, result] = useMutation(stationId ? updateStationCampaignMutation : updateCampaignMutation)

  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value)
  }

  function onDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDescription(e.target.value)
  }

  function onSubmit() {
    updateCampaign({
      variables: {
        id: campaign!.id,
        stationId,
        input: {
          name,
          startTime: starts?.toISOString(),
          endTime: ends?.toISOString(),
          description,
        },
      },
    }).then(() => {
      onClose()
    })
  }

  useEffect(() => {
    setName(campaign?.name)
    setStarts(moment(campaign?.startTime))
    setEnds(moment(campaign?.endTime))
    setDescription(campaign?.description ?? '')
  }, [campaign])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogContent>
        <CampaignFormFields
          title={i18n.t('campaigns.editCampaign')}
          name={name ?? ''}
          starts={starts}
          ends={ends}
          description={description}
          onNameChange={onNameChange}
          onStartsChange={setStarts}
          onEndsChange={setEnds}
          onDescriptionChange={onDescriptionChange}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={onClose}>
          {i18n.t('buttons.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!name || !starts || !ends || result.loading}
          onClick={onSubmit}
        >
          {i18n.t('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
